import { QUOTE_CLASSIFICATION } from 'src/constants/common';

// Lane context forwarded down from the quote payload (QuoteDetail's
// `quotingDetails`). Every field is optional: on a direct page load
// before the quote finishes fetching there may be no context yet, and the
// loader must degrade gracefully (timeline only, no lane panel).
export interface IQuotePreparingLaneContext {
    classification?: string;
    // Server-derived (quote detail payload, `isInstantLtl`): true = standard LTL that rates
    // automatically; false = Volume LTL / FTL / Specialized, priced by a sales rep. Drives which
    // wording the loader's timeline uses. undefined until the quote payload lands — the loader
    // then keeps the instant wording rather than guessing the customer is waiting on a human.
    isInstantLtl?: boolean;
    shipperCity?: string;
    shipperStateCode?: string;
    shipperCountryCode?: string;
    shipperLatitude?: string | number;
    shipperLongitude?: string | number;
    consigneeCity?: string;
    consigneeStateCode?: string;
    consigneeCountryCode?: string;
    consigneeLatitude?: string | number;
    consigneeLongitude?: string | number;
    totalUnits?: number;
    handlingUnitName?: string;
    totalWeight?: number;
    // 'lbs' | 'kgs' — weight is only used when its unit is known
    weightUnitLabel?: string;
    // Date or datetime string — drives timing/seasonal cards. Bare YYYY-MM-DD
    // is parsed as LOCAL time (see parsePickupMonthAndDay), not UTC.
    pickupDate?: string;
    // Any dimension row marked non-stackable → stackable pricing tip
    hasNonStackablePallets?: boolean;
    // Liftgate/tailgate at delivery selected → dock tip (win-win gate W3)
    hasTailgateDelivery?: boolean;
    // Shipment density; null/undefined when dims incomplete
    densityLbsPerCuft?: number | null;
    // Real driving distance in miles (Mapbox Directions API, same source as
    // the Map-It widget on this page) — NOT a straight-line estimate. A
    // haversine "as the crow flies" number was tried first and rejected: it
    // reads as flat-out wrong on routes that can't go direct (e.g. Richmond,
    // BC → Toronto, ON is ≈2,090 mi haversine vs ≈2,726 mi actually driven).
    // null/undefined while the lookup hasn't resolved yet — distance-based
    // cards are omitted rather than showing a number we know is inaccurate.
    drivingMiles?: number | null;
}

// One card in the "Lane intelligence" journey stack. Slots follow the
// shipment's journey: pickup → road → border → delivery. body uses **bold**
// markers rendered as emphasis by the component.
export interface IJourneyCard {
    slot: 'pickup' | 'road' | 'border' | 'delivery';
    kicker: string;
    headline: string;
    body: string;
}

export const isLtl = (classification?: string) => classification === QUOTE_CLASSIFICATION.LTL;

// Road-transit heuristic per service type. Deliberately coarse ranges —
// customer-facing expectation setting, not a promise.
const transitEstimate = (miles: number, classification?: string): string => {
    if (isLtl(classification)) {
        if (miles < 500) return '1–2 business days';
        if (miles < 1200) return '2–4 business days';
        if (miles < 2000) return '3–5 business days';

        return '4–6 business days';
    }

    if (miles < 400) return 'next day';
    if (miles < 900) return '1–2 days';
    if (miles < 1600) return '2–3 days';

    return '3–5 days';
};

export const isCrossBorder = (ctx: IQuotePreparingLaneContext) =>
    Boolean(ctx.shipperCountryCode && ctx.consigneeCountryCode && ctx.shipperCountryCode.toUpperCase() !== ctx.consigneeCountryCode.toUpperCase());

// ---------------------------------------------------------------------------
// Lane lookup table (Phase A of the Lane Intelligence scope).
// Likely border crossing + interstate corridor per origin/destination region
// pair, for the corridors WAL actually quotes. Matched in either direction.
// Phase B replaces/augments these with real operating-history insights from
// the /lane-insights endpoint — the card shapes stay identical.
// ---------------------------------------------------------------------------
interface ILaneRule {
    caSide: string[];
    usSide: string[];
    crossing: string;
    corridor: string;
}

const LANE_RULES: ILaneRule[] = [
    {
        caSide: ['ON'],
        usSide: ['WA', 'OR', 'ID', 'MT', 'ND', 'SD', 'MN', 'WI'],
        crossing: 'Sarnia–Port Huron',
        corridor: 'the I-90/94 corridor through Chicago and Minneapolis',
    },
    {
        caSide: ['ON'],
        usSide: ['CA', 'NV', 'AZ', 'UT', 'CO', 'NE', 'IA'],
        crossing: 'Windsor–Detroit (Ambassador Bridge)',
        corridor: 'the I-80 corridor through Chicago',
    },
    {
        caSide: ['ON'],
        usSide: ['NY', 'NJ', 'PA', 'MA', 'CT', 'RI', 'VT', 'NH', 'ME', 'MD', 'DE'],
        crossing: 'Fort Erie–Buffalo (Peace Bridge)',
        corridor: 'the I-90 corridor',
    },
    {
        caSide: ['ON'],
        usSide: ['MI', 'OH', 'IN', 'IL', 'KY', 'TN'],
        crossing: 'Windsor–Detroit (Ambassador Bridge)',
        corridor: 'the I-75/I-94 corridor',
    },
    {
        caSide: ['ON'],
        usSide: ['TX', 'OK', 'AR', 'LA', 'MO', 'KS'],
        crossing: 'Windsor–Detroit (Ambassador Bridge)',
        corridor: 'the I-55/I-44 corridor through Chicago and St. Louis',
    },
    {
        caSide: ['ON'],
        usSide: ['GA', 'FL', 'NC', 'SC', 'VA', 'WV', 'AL', 'MS'],
        crossing: 'Fort Erie–Buffalo (Peace Bridge)',
        corridor: 'the I-77/I-95 corridor',
    },
    {
        caSide: ['QC'],
        usSide: ['NY', 'NJ', 'PA', 'MA', 'CT', 'RI', 'VT', 'NH', 'ME'],
        crossing: 'Lacolle–Champlain',
        corridor: 'the I-87/I-95 corridor',
    },
    {
        caSide: ['BC'],
        usSide: ['WA', 'OR', 'CA', 'NV', 'AZ'],
        crossing: 'Pacific Highway (Blaine)',
        corridor: 'the I-5 corridor',
    },
    {
        caSide: ['AB', 'SK'],
        usSide: ['MT', 'WY', 'ID', 'UT', 'CO'],
        crossing: 'Coutts–Sweet Grass',
        corridor: 'the I-15 corridor',
    },
    {
        caSide: ['MB'],
        usSide: ['ND', 'SD', 'NE', 'MN', 'IA', 'MO'],
        crossing: 'Emerson–Pembina',
        corridor: 'the I-29 corridor',
    },
];

const findLaneRule = (originState?: string, destState?: string): ILaneRule | null => {
    if (!originState || !destState) return null;
    const o = originState.toUpperCase();
    const d = destState.toUpperCase();

    return LANE_RULES.find((rule) => (rule.caSide.includes(o) && rule.usSide.includes(d)) || (rule.caSide.includes(d) && rule.usSide.includes(o))) || null;
};

const parsePickupMonthAndDay = (pickupDate?: string): { month: number; weekday: number } | null => {
    if (!pickupDate) return null;

    // Bare YYYY-MM-DD parses as UTC midnight in JS, shifting the weekday back
    // a day in North American timezones — parse date-only strings as local.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(pickupDate.trim());
    const date = dateOnly ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])) : new Date(pickupDate);

    if (Number.isNaN(date.getTime())) return null;

    return { month: date.getMonth() + 1, weekday: date.getDay() };
};

// Winter buffer applies November–March on cross-border/northern lanes
const isWinterMonth = (month: number) => month >= 11 || month <= 3;

const cityLabel = (city?: string, stateCode?: string) => (city ? (stateCode ? `${city}, ${stateCode}` : city) : '');

// transitEstimate() is also used mid-sentence (lowercase is correct there,
// e.g. "...typically **next day** door to door."); headlines are standalone
// titles and need the leading word capitalized, per the approved mockup
// ("Next day door to door" vs the inline "next day").
const capitalize = (value: string) => (value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value);

// Density below this reads as "light freight" in LTL — denser packing tips
// apply. Coarse on purpose; refined thresholds arrive with Phase B data.
const LOW_DENSITY_LBS_PER_CUFT = 6;

// ---------------------------------------------------------------------------
// Journey card builder. One card per slot (pickup → road → border →
// delivery); within a slot the first applicable insight wins. Cards without
// backing data are omitted, never faked.
//
// HARD RULE (discretion): cards describe the lane and the customer's own
// shipment only — never rate counts, carrier counts, pricing rules, or
// anything about the rating process.
// ---------------------------------------------------------------------------
export const buildJourneyCards = (ctx: IQuotePreparingLaneContext): IJourneyCard[] => {
    const cards: IJourneyCard[] = [];
    const ltl = isLtl(ctx.classification);
    const crossBorder = isCrossBorder(ctx);
    const miles = ctx.drivingMiles ?? null;
    const rule = findLaneRule(ctx.shipperStateCode, ctx.consigneeStateCode);
    const pickup = parsePickupMonthAndDay(ctx.pickupDate);
    const originLabel = cityLabel(ctx.shipperCity, ctx.shipperStateCode);
    const destLabel = cityLabel(ctx.consigneeCity, ctx.consigneeStateCode);

    // --- Pickup slot: load-prep and timing money tips ---
    const pickupKicker = originLabel ? `Pickup — ${originLabel}` : 'Pickup';

    if (ltl && ctx.hasNonStackablePallets) {
        cards.push({
            slot: 'pickup',
            kicker: pickupKicker,
            headline: 'Stackable pallets price better',
            body: 'Marking pallets stackable lets carriers use trailer space better — **that usually shows up in your rate**.',
        });
    } else if (ltl && typeof ctx.densityLbsPerCuft === 'number' && ctx.densityLbsPerCuft > 0 && ctx.densityLbsPerCuft < LOW_DENSITY_LBS_PER_CUFT) {
        cards.push({
            slot: 'pickup',
            kicker: pickupKicker,
            headline: 'Denser freight prices better',
            body: `At ~${ctx.densityLbsPerCuft.toFixed(1)} lbs per cubic foot your freight is on the lighter side — **tighter, denser packing** consistently improves LTL pricing.`,
        });
    } else if (pickup && (pickup.weekday === 5 || pickup.weekday === 6)) {
        cards.push({
            slot: 'pickup',
            kicker: pickupKicker,
            headline: 'Early-week pickups price best',
            body: 'Carriers price **Monday–Wednesday pickups** best on most corridors — a one-day shift can matter.',
        });
    } else if (crossBorder) {
        cards.push({
            slot: 'pickup',
            kicker: pickupKicker,
            headline: 'Early-week pickups travel best',
            body: 'Freight picked up Monday–Tuesday on this lane usually **clears the border before the weekend**.',
        });
    } else if (ltl) {
        cards.push({
            slot: 'pickup',
            kicker: pickupKicker,
            headline: 'Accurate weight avoids fees',
            body: 'Accurate declared weight avoids **carrier reweigh fees** after pickup — the most common surprise charge in LTL.',
        });
    }

    // --- Road slot: distance + corridor ---
    if (miles !== null) {
        cards.push({
            slot: 'road',
            kicker: 'The road',
            headline: `≈ ${miles.toLocaleString()} miles`,
            body: rule
                ? `Most carriers run this via **${rule.corridor}**.`
                : originLabel && destLabel
                ? `${originLabel} → ${destLabel} — typically **${transitEstimate(miles, ctx.classification)}** door to door.`
                : `Typically **${transitEstimate(miles, ctx.classification)}** door to door.`,
        });
    } else if (rule) {
        cards.push({
            slot: 'road',
            kicker: 'The road',
            headline: 'A well-worn corridor',
            body: `Most carriers run this via **${rule.corridor}**.`,
        });
    }

    // --- Border slot: cross-border lanes only ---
    if (crossBorder) {
        cards.push({
            slot: 'border',
            kicker: 'The border',
            headline: rule ? `Likely crossing: ${rule.crossing}` : 'An international crossing',
            body: 'A completed **commercial invoice** avoids the most common customs delay.',
        });
    }

    // --- Delivery slot: accessorial money tip > winter buffer > transit ---
    // Winter outranks the generic transit estimate Nov–Mar on cross-border
    // lanes: the buffer advice is more valuable than repeating a number, and
    // gating it behind missing coordinates made it unreachable in practice.
    const deliveryKicker = destLabel ? `Delivery — ${destLabel}` : 'Delivery';

    if (ctx.hasTailgateDelivery) {
        cards.push({
            slot: 'delivery',
            kicker: deliveryKicker,
            headline: 'Have a dock at delivery?',
            body: 'Liftgate at delivery adds cost — if your receiver has a dock, **you can drop it** from this quote.',
        });
    } else if (pickup && isWinterMonth(pickup.month) && crossBorder) {
        cards.push({
            slot: 'delivery',
            kicker: deliveryKicker,
            headline: 'Winter buffer worth having',
            body: 'Winter weather on northern segments can add a day November–March — **build buffer into tight deliveries**.',
        });
    } else if (miles !== null) {
        cards.push({
            slot: 'delivery',
            kicker: deliveryKicker,
            headline: `${capitalize(transitEstimate(miles, ctx.classification))} door to door`,
            body: 'Typical for this lane — **exact transit comes with your quote**.',
        });
    }

    return cards;
};
